import React, { useEffect, useState } from 'react';
import type { ScheduledSession } from '../../services/schedulingService';
import { formatSessionTimeInSeekerTimezone, getBrowserTimezone, getTimezoneAbbreviation } from '../../utils/dateUtils';
import { seekerDataService } from '../../services/seekerDataService';

type BookingSuccessProps = {
    session: ScheduledSession;
    onContinue?: () => void;
};

export const BookingSuccess: React.FC<BookingSuccessProps> = ({
    session,
    onContinue,
}) => {
    const [seekerTimezone, setSeekerTimezone] = useState<string | null>(null);

    // Load seeker timezone on mount
    useEffect(() => {
        const loadTimezone = async () => {
            const tz = await seekerDataService.getSeekerTimezone();
            setSeekerTimezone(tz || getBrowserTimezone());
        };
        loadTimezone();
    }, []);

    const { formattedDate, formattedTime } = formatSessionTimeInSeekerTimezone(
        session.start_time,
        seekerTimezone,
        session.session_date,
        session.session_time
    );

    // Append timezone abbreviation
    const tz = seekerTimezone || getBrowserTimezone();
    const tzAbbrev = session.start_time && tz ? getTimezoneAbbreviation(session.start_time, tz) : '';
    const displayTime = tzAbbrev ? `${formattedTime} ${tzAbbrev}` : formattedTime;

    return (
        <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
            <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
                <header className="flex h-16 items-center border-b border-[#FEF6D6]/25 bg-[#2C2731] px-4">
                    <div className="w-10" aria-hidden />
                    <div className="flex flex-1 justify-center">
                        <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
                    </div>
                    <div className="w-10" aria-hidden />
                </header>

                <main className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto px-6 py-6">
                    <div className="rounded-[32px] border border-[#304031]/20 bg-[#FFF8E1]/90 p-6 text-center shadow-[0_32px_60px_-35px_rgba(32,77,60,0.45)]">
                        <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full border border-[#304031]/15 bg-[#FFFCEF] text-[#304031]">
                            <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">
                            Confirmation sent
                        </p>
                        <h1 className="mt-2 font-recoleta text-[32px] font-normal leading-tight text-[#304031]">
                            Your consultation is booked!
                        </h1>
                        <p className="mt-3 text-sm text-[#214D3C]">
                            We've emailed your confirmation and preparation notes. You'll also receive a reminder 24 hours before.
                        </p>
                    </div>

                    <div className="rounded-2xl border border-[#304031]/18 bg-white p-5 shadow-[0_30px_60px_-40px_rgba(15,8,27,0.45)]">
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">
                            Session details
                        </p>
                        <div className="mt-4 space-y-3 text-sm text-[#214D3C]">
                            <div>
                                <span className="font-semibold text-[#304031]">Date:</span> {formattedDate}
                            </div>
                            <div>
                                <span className="font-semibold text-[#304031]">Time:</span> {displayTime}
                            </div>
                            {session.guide_name && (
                                <div>
                                    <span className="font-semibold text-[#304031]">Guide:</span> {session.guide_name}
                                </div>
                            )}
                            {session.id ? (
                                <div>
                                    <span className="font-semibold text-[#304031]">Reference:</span> #{session.id.slice(0, 8)}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className="rounded-2xl border border-[#304031]/15 bg-[#FFFCEF] p-5">
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">
                            Next steps
                        </p>
                        <ul className="mt-3 space-y-3 text-sm text-[#214D3C]">
                            <li className="flex items-start gap-3">
                                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#304031] text-white">
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                </span>
                                <span>Account created</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#304031] text-white">
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                </span>
                                <span>Consultation booked</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-[#304031]/30">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#304031]/30" />
                                </span>
                                <span>Check your inbox for a confirmation email and calendar invite</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-[#304031]/30">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#304031]/30" />
                                </span>
                                <span>Think about any questions you'd like to ask your guide</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-[#304031]/30">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#304031]/30" />
                                </span>
                                <span>You'll receive a reminder 24 hours before your session</span>
                            </li>
                        </ul>
                    </div>
                </main>

                <footer className="sticky bottom-0 z-10 mt-auto border-t border-[#2C2731]/18 bg-gradient-to-t from-[#FEF6D6] via-[#FEF6D6] to-[#FEF6D6]/0 px-6 pb-7 pt-5 shadow-[0_-12px_26px_-24px_rgba(44,39,49,0.55)]">
                    <div className="w-full">
                        <button
                            type="button"
                            onClick={onContinue}
                            className="group inline-flex w-full items-center justify-between gap-3 rounded-full border border-[#304031] bg-[#FFFCEF] px-6 py-4 text-lg font-medium text-[#304031] shadow-[0_16px_36px_-20px_rgba(48,64,49,0.55)] transition-transform transition-colors duration-200 hover:-translate-y-0.5 hover:bg-[#304031] hover:text-[#FEF6D6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#304031]"
                        >
                            <span className="invisible w-5 text-xl leading-none">{'\u2190'}</span>
                            <span className="font-medium">Go to homepage</span>
                            <span className="w-5 text-xl leading-none">{'\u2192'}</span>
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
};
